import { TeamConferenceMap, TeamRivalries } from '../constants/league-constants';
import { TeamBasicContext } from '../types/game-enrichment.types';
import { GameID, TeamAbrvID, TeamAbrvType } from '../types/id.types';
import { breakGameID, toTeamAbrvID } from './id-helpers';

/**
 * Determines if a game is a conference matchup (both teams in same conference).
 *
 * @param {GameID} gameID - The game identifier
 *
 * @return {boolean} - True if both teams are in the same conference, false otherwise
 *
 * @example
 * isConferenceMatch('20240905_BAL@KC');  // true (both AFC)
 * isConferenceMatch('20240908_PHI@GB');  // false (NFC vs NFC)
 */
export const isConferenceMatch = (gameID: GameID): boolean => {
  const { awayTeamAbrv, homeTeamAbrv } = breakGameID(gameID);
  return TeamConferenceMap[awayTeamAbrv] === TeamConferenceMap[homeTeamAbrv as TeamAbrvType];
};

/**
 * Determines if two teams are rivals based on team context and rivalry maps.
 *
 * @description
 * Checks both team context rivalry lists and the local TeamRivalries map to
 * determine if teams have a rivalry relationship. Used for game context enrichment.
 *
 * @param {TeamBasicContext} teamA - First team context object
 * @param {TeamBasicContext} teamB - Second team context object
 *
 * @return {boolean} - True if teams are rivals, false otherwise
 *
 * @example
 * areRivalTeams(packers, bears);  // true (historic NFC North rivalry)
 */
export const areRivalTeams = (teamA: TeamBasicContext, teamB: TeamBasicContext): boolean => {
  const aTeamAbrvID = toTeamAbrvID(teamA.id);
  const bTeamAbrvID = toTeamAbrvID(teamB.id);
  return (
    teamA.rivalries.includes(bTeamAbrvID) ||
    teamB.rivalries.includes(aTeamAbrvID) ||
    areRivalTeamsFromLocalMap(aTeamAbrvID, bTeamAbrvID)
  );
};

/**
 * Checks if two teams are rivals using the local TeamRivalries constant.
 *
 * @param {TeamAbrvID} teamA - First team abbreviation ID
 * @param {TeamAbrvID} teamB - Second team abbreviation ID
 *
 * @return {boolean} - True if both teams list each other as rivals
 *
 * @example
 * areRivalTeamsFromLocalMap('11-DET', '12-GB');  // true (division rivals)
 */
export const areRivalTeamsFromLocalMap = (teamA: TeamAbrvID, teamB: TeamAbrvID): boolean => {
  return TeamRivalries[teamA].includes(teamB) && TeamRivalries[teamB].includes(teamA);
};
